import React from "react";
import { render, unmountComponentAtNode } from "react-dom";

import { GenericControlComponentProps } from "@multiweb/reactComponents";
import { createRootComponent, RootComponentClass, StoreManager } from "@multiweb/reactSetup";
import { EMPTY_OBJECT, invariant } from "@multiweb/reactUtils";
import { ReactComponent } from "@multiweb/types/react";
import { AppContext } from "../../../App";
import { IItemRightsCollection } from "../../../Global/Base/Rights/RightTypes";
import { EventsController } from "../../../Global/Dom/EventsController";
import {
    ICompositeComponent,
    IGenericControl,
} from "../../UI/UI/Controls/ControlInterfaces";

/**
 * Pomocný typ pro naplnění hlavních atributů v konektoru viz {@link GenericControlConnector}.
 */
export type GenericControlConnectorAttrs = Readonly<{
    /** Unikátní identifikátor komponenty. Id je určeno primárně pro input element v komponentě. */
    componentId: string | undefined;
    rootElement: HTMLElement;
    component: ReactComponent;
    isCompositeComponent?: boolean;
    originalRightsDefinition: IItemRightsCollection;
}>;

/**
 * Umožňuje napojení react komponent do původní multiweb aplikace.
 * Do konektoru je skrze konstruktor vložena react komponenta, které jsou následně předány props důležité pro její vykreslování.
 * Konektor implementuje stejná rozhraní jako základní control {@link GenericControlConnector} a zastupuje jeho funkci.
 */
export class GenericControlConnector implements IGenericControl, ICompositeComponent {
    /** Kořenový element, do kterého se vkládá kořenová komponenta. */
    protected readonly _rootElement: HTMLElement;
    /** Kořenová komponenta, do které se vkládá předaná react komponenta. Poskytuje pro své potomky přístup k redux stavu a zachytává chyby z komponent, reducerů a ság. */
    protected readonly _rootComponent: RootComponentClass;
    /** React komponenta, která se bude vykreslovat. */
    protected readonly _component: ReactComponent;
    /** Příznak, jestli se jedná o kompozitní komponentu (složenou z dalších komponent) viz {@link ICompositeComponent}.*/
    protected _isCompositeComponent?: boolean;
    /** Viz {@link IWithOriginalRightsDefinition.originalRightsDefinition}. Atribut je protected z důvodu problému uvedeném v mixinu `CursorValueAdjustableMixin`. */
    protected readonly _originalRightsDefinition: IItemRightsCollection;
    /** Poskytuje možnost registrovat posluchače událostí, které se automaticky odpojí při dispose GenericControlConnectoru. */
    protected readonly _eventsController = new EventsController();

    /**
     * Inicializuje store a vytvoří kořenovou komponentu do které se následně vkládá react komponenta. Kořenová komponenta se vkládá do kořenového elementu.
     * @param attrs Hlavní atributy konektoru
     * @param canRender Příznak, zda se může komponenta vykreslit hned po inicializaci konektoru.
     * Ve výchozím nastavení (`true`) je komponenta vykreslena po volání {@link _renderIfTrue} v konstruktoru
     */
    constructor(attrs: GenericControlConnectorAttrs, canRender = true) {
        invariant(!!attrs.originalRightsDefinition, "[GenericControlConnector] originalRightsDefinition must be present");
        this._rootElement = attrs.rootElement;
        this._component = React.memo(attrs.component);
        this._isCompositeComponent = attrs.isCompositeComponent;
        this._originalRightsDefinition = attrs.originalRightsDefinition;

        const isDev = !!AppContext.instance.configuration.developmentMode;
        StoreManager.initializeStore(isDev);
        this._rootComponent = createRootComponent(isDev);

        this._renderIfTrue(canRender);

        this.element.classList.add("ControlConnector");
    }

    /**
     * Metoda pro podmíněné vykreslení volána v konstruktoru.
     * @param canRender Příznak, zda se může komponenta vykreslit.
     * @protected
     */
    protected _renderIfTrue(canRender: boolean): void {
        if (canRender) {
            this._render();
        }
    }

    public get element(): HTMLElement {
        return this._rootElement;
    }

    public get originalRightsDefinition(): IItemRightsCollection {
        return this._originalRightsDefinition;
    }

    public get isCompositeComponent(): boolean | undefined {
        return this._isCompositeComponent;
    }

    public set isCompositeComponent(value: boolean | undefined) {
        this._isCompositeComponent = value;
    }

    /**
     * Zkonvertuje atributy ovlivňující podobu vykreslované react komponenty do props.
     * @protected
     */
    protected _getRenderProps(): GenericControlComponentProps {
        return EMPTY_OBJECT;
    }

    /**
     * Metoda pro vykreslování react komponenty.
     * @protected
     */
    protected _render(): void {
        const genericComponentProps = this._getRenderProps();
        render(
            <this._rootComponent>
                <this._component {...genericComponentProps} />
            </this._rootComponent>,
            this._rootElement,
        );
    }

    /**
     * Uvolní alokované zdroje.
     */
    public dispose(): void {
        unmountComponentAtNode(this._rootElement);
        this._rootElement.remove();
        this._eventsController.dispose();
    }
}
